/**
 * WebSocket service for real-time updates
 */
class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 5000; // 5 seconds
    this.listeners = new Map();
    this.isConnecting = false;
    this.authenticated = false;
    this.userId = null;
    // Channels components asked for, replayed on reconnect: the server forgets
    // a socket's subscriptions when it drops.
    this.subscriptions = new Set();
  }

  /**
   * Connect to WebSocket server
   * @param {string} url - WebSocket URL
   * @returns {Promise<void>}
   */
  connect(url = null) {
    return new Promise((resolve, reject) => {
      if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
        console.log(`[WS-CLIENT] connect() skipped: already ${this.socket.readyState === WebSocket.OPEN ? 'OPEN' : 'CONNECTING'}`);
        resolve();
        return;
      }

      if (this.isConnecting) {
        console.log('[WS-CLIENT] connect() skipped: isConnecting=true');
        resolve();
        return;
      }

      this.isConnecting = true;
      this._connectTime = Date.now();

      // Use provided URL or construct from current location
      const wsUrl = url || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
      console.log(`[WS-CLIENT] Connecting to: ${wsUrl}`);

      try {
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          const elapsed = Date.now() - this._connectTime;
          console.log(`[WS-CLIENT] CONNECTED in ${elapsed}ms, readyState=${this.socket.readyState}, protocol="${this.socket.protocol}", extensions="${this.socket.extensions}"`);
          this.isConnecting = false;
          this.reconnectAttempts = 0;

          // Wait a bit before authenticating to ensure server is ready
          setTimeout(() => {
            // Check socket is still open before sending subscriptions
            if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
              console.warn(`[WS-CLIENT] Socket closed before subscriptions could be sent! readyState=${this.socket ? this.socket.readyState : 'null'}`);
              return;
            }

            // Authenticate if we have a user ID
            if (this.userId) {
              this.authenticate(this.userId);
            }

            // Subscribe to general updates
            // 'activity-log' used to be subscribed here and had no listener
            // anywhere; access-control traffic now rides 'door-activity',
            // which the components that draw it subscribe to themselves.
            this.subscribe('door-activity');
            this.subscribe('evacuation-status');
            this.subscribe('elevator-status');
            this.subscribe('google-forms-webhook-tasks');
            this.subscribe('presence');
            this.subscribe('userOnlineStatus');
            this.subscribe('notification');

            // Subscribe to messaging updates
            this.subscribe('new_message');
            this.subscribe('messages_read');
            this.subscribe('message_deleted');
            this.subscribe('typing_status');
            this.subscribe('reaction_added');

            // Subscribe to BMS real-time updates (reusing existing websocket infrastructure)
            this.subscribe('door-status');
            this.subscribe('camera-status');
            this.subscribe('temperature-updates');
            this.subscribe('hvac-status');
            this.subscribe('safety-alerts');
            this.subscribe('wifi-status');
            this.subscribe('emergency-alerts');
            this.subscribe('system-health');

            this.subscriptions.forEach(eventType => this.subscribe(eventType));

            console.log('[WS-CLIENT] All subscriptions sent');
          }, 500); // Wait 500ms for server to be ready

          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('[WS-CLIENT] Error parsing message:', error);
          }
        };

        this.socket.onclose = (event) => {
          const uptime = this._connectTime ? Date.now() - this._connectTime : 'unknown';
          console.log(`[WS-CLIENT] CLOSED: code=${event.code}, reason="${event.reason}", wasClean=${event.wasClean}, uptime=${uptime}ms, reconnectAttempts=${this.reconnectAttempts}`);
          this.isConnecting = false;
          this.socket = null;
          this.authenticated = false;

          // Attempt to reconnect if not a normal closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            console.log(`[WS-CLIENT] Will reconnect in ${this.reconnectInterval / 1000}s (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
            this.scheduleReconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('[WS-CLIENT] Max reconnection attempts reached, giving up');
          } else {
            console.log('[WS-CLIENT] Normal closure (code=1000), not reconnecting');
          }
        };

        this.socket.onerror = (error) => {
          console.error(`[WS-CLIENT] ERROR: type=${error.type}, readyState=${this.socket ? this.socket.readyState : 'null'}, url=${wsUrl}`, error);
          this.isConnecting = false;
          reject(error);
        };

      } catch (error) {
        console.error(`[WS-CLIENT] Failed to create WebSocket: ${error.message}`);
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * Schedule a reconnection attempt
   */
  scheduleReconnect() {
    this.reconnectAttempts++;
    console.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
    
    setTimeout(() => {
      this.connect().catch(error => {
        console.error('Reconnection failed:', error);
      });
    }, this.reconnectInterval);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
    this.listeners.clear();
  }

  /**
   * Authenticate user with WebSocket server
   * @param {string} userId - User ID to authenticate with
   */
  authenticate(userId) {
    this.userId = userId;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'authenticate',
        userId: userId
      }));
    }
  }

  /**
   * Set user ID for authentication on connection
   * @param {string} userId - User ID
   */
  setUserId(userId) {
    this.userId = userId;
    if (this.socket && this.socket.readyState === WebSocket.OPEN && !this.authenticated) {
      this.authenticate(userId);
    }
  }

  /**
   * Subscribe to a specific event type
   * @param {string} eventType - Event type to subscribe to
   */
  subscribe(eventType) {
    this.subscriptions.add(eventType);
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'subscribe',
        eventType: eventType
      }));
    }
  }

  /**
   * Unsubscribe from a specific event type
   * @param {string} eventType - Event type to unsubscribe from
   */
  unsubscribe(eventType) {
    this.subscriptions.delete(eventType);
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'unsubscribe',
        eventType: eventType
      }));
    }
  }

  /**
   * Add event listener
   * @param {string} eventType - Event type to listen for
   * @param {Function} callback - Callback function
   */
  addEventListener(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
  }

  /**
   * Remove event listener
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function to remove
   */
  removeEventListener(eventType, callback) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).delete(callback);
      
      // Clean up empty listener sets
      if (this.listeners.get(eventType).size === 0) {
        this.listeners.delete(eventType);
      }
    }
  }

  /**
   * Handle incoming WebSocket message
   * @param {Object} data - Parsed message data
   */
  handleMessage(data) {
    const { type, eventType, payload, userId } = data;

    if (type === 'authenticated') {
      console.log('WebSocket authenticated for user:', userId);
      this.authenticated = true;
    } else if (type === 'subscribed') {
      console.log('WebSocket subscribed to:', data.eventType);
    } else if (type === 'event' && eventType) {
      console.log('WebSocket event received:', eventType, payload?.data ? 'with data' : 'no data');
      // Emit event to registered listeners
      if (this.listeners.has(eventType)) {
        this.listeners.get(eventType).forEach(callback => {
          try {
            callback(payload);
          } catch (error) {
            console.error('Error in WebSocket event listener:', error);
          }
        });
      } else {
        console.log('No listeners found for event:', eventType);
      }
    } else if (type === 'error') {
      console.error('WebSocket server error:', data.message);
    }
  }

  /**
   * Send message to server
   * @param {Object} message - Message to send
   */
  send(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  /**
   * Get connection status
   * @returns {boolean} - True if connected
   */
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state
   * @returns {string} - Connection state
   */
  getConnectionState() {
    if (!this.socket) return 'disconnected';
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
